import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { ModalFinishByPhoneComponent } from 'src/app/components/modals/modal-finish-by-phone/modal-finish-by-phone.component';
import { phoneNumberValidator } from 'src/app/directives/phone-number/phone-number.directive';
import { zipCodeValidator } from 'src/app/directives/zip-code/zip-code.directive';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import { BranchService } from 'src/app/services/branch/branch.service';
import { DropdownMenus } from 'src/app/services/dropdowns/dropdowns.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { FinalLoanApplication } from 'src/app/services/loan/loan.model';
import { LoanService } from 'src/app/services/loan/loan.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { RouteName } from '../loan-routing.module';

@Component({
  selector: 'app-co-applicant-employer',
  templateUrl: './co-applicant-employer.component.html',
  styleUrls: ['./co-applicant-employer.component.scss'],
})
export class CoApplicantEmployerComponent implements OnInit, OnDestroy {
  coApplicantEmployerFormGroup: FormGroup;
  otherIncomeFormGroup: FormGroup;
  loanApplication: FinalLoanApplication | null = null;
  dropdownMenus: DropdownMenus | null = null;
  isDirtyAndMissingRequiredFields = false;
  isOtherIncomeDirtyAndMissingRequiredFields = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private loanService: LoanService,
    private navService: NavigationService,
    private branchService: BranchService,
    private router: Router,
    private dropdownsService: DropdownsService,
    private modalService: NgbModal,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle('Eagle Loan | Co-Applicant Employer');
    this.navService.setBackNav(
      'Co-Applicant Home...',
      RouteName.coApplicantHomeVehicles
    );
    this.branchService.setShowInfoButton(true);

    this.coApplicantEmployerFormGroup = this.formBuilder.group({
      EmployerName: ['', Validators.required],
      Position: ['', Validators.required],
      Years: ['', [Validators.required, Validators.min(0)]],
      Months: [
        '',
        [Validators.required, Validators.min(0), Validators.max(12)],
      ],
      Address: ['', Validators.required],
      AptNumber: [{ value: '', disabled: true }],
      POBox: [{ value: '', disabled: true }],
      City: ['', Validators.required],
      State: ['', Validators.required],
      ZipCode: ['', [Validators.required, zipCodeValidator]],
      PhoneNumber: ['', [Validators.required, phoneNumberValidator]],
      PhoneNumberType: [{ value: '', disabled: true }],
      EmailAddress: [{ value: '', disabled: true }],
      PayTypeCD: ['', Validators.required],
      PayPeriod: ['', Validators.required],
      TakeHomePay: ['', [Validators.required, Validators.min(0)]],
      AnnualSalary: ['', [Validators.required, Validators.min(0)]],
      NetIncomePPP: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      HoursPerWeek: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      HourlyRate: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      OtherIncomes: [''],
    });

    this.otherIncomeFormGroup = this.formBuilder.group({
      OtherIncomes: [[]],
      MonthlyAdditionalIncome: ['', Validators.min(0)],
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.loanApplication = { ...data };

            if (this.loanApplication.LoanApplication) {
              // Make monthly additional income required if other incomes has selections.
              if (
                this.loanApplication.CoApplicantAdditionalIncome
                  ?.OtherIncomes &&
                this.loanApplication.CoApplicantAdditionalIncome?.OtherIncomes
                  .length > 0
              ) {
                this.otherIncomeFormGroup.controls[
                  'MonthlyAdditionalIncome'
                ].addValidators([Validators.required, Validators.min(0)]);
              }
            }
          }
        }
      )
    );

    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          this.dropdownMenus = null;

          if (data) {
            this.dropdownMenus = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.coApplicantEmployerFormGroup.statusChanges.subscribe(
        (status: string) => {
          if (status && status === 'VALID') {
            this.isDirtyAndMissingRequiredFields = false;
          }
        }
      )
    );

    this.subscriptions.push(
      this.otherIncomeFormGroup.statusChanges.subscribe((status: string) => {
        if (status && status === 'VALID') {
          this.isOtherIncomeDirtyAndMissingRequiredFields = false;
        }
      })
    );

    // On refresh of the page it will redirect to start of application.
    if (!this.loanApplication) {
      this.router.navigateByUrl(RouteName.default, { replaceUrl: true });
    }

    if (!this.dropdownMenus) {
      this.dropdownsService.getDropdowns();
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  onNext() {
    this.isDirtyAndMissingRequiredFields = false;
    this.isOtherIncomeDirtyAndMissingRequiredFields = false;

    if (
      this.coApplicantEmployerFormGroup.invalid ||
      this.otherIncomeFormGroup.invalid
    ) {
      if (this.coApplicantEmployerFormGroup.invalid) {
        this.coApplicantEmployerFormGroup.markAllAsTouched();
        this.isDirtyAndMissingRequiredFields = true;
      }

      if (this.otherIncomeFormGroup.invalid) {
        this.otherIncomeFormGroup.markAllAsTouched();
        this.isOtherIncomeDirtyAndMissingRequiredFields = true;
      }

      return;
    }

    this.loanService.setApplicantEmployer(
      {
        ...this.coApplicantEmployerFormGroup.value,
        ...this.otherIncomeFormGroup.value,
      },
      true
    );

    this.router.navigateByUrl(RouteName.bank);
  }

  async onFinishByPhone(): Promise<void> {
    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Finish By Phone | Co-Applicant Employer'
    );

    const modalRef = this.modalService.open(ModalFinishByPhoneComponent);

    modalRef.componentInstance.loan = this.loanService.setApplicantEmployer(
      {
        ...this.coApplicantEmployerFormGroup.value,
        ...this.otherIncomeFormGroup.value,
      },
      true
    );

    const result = await modalRef.result;

    if (result && result.status === 'confirmed') {
      this.onConfirmed();
    }
  }

  onConfirmed() {
    this.loanService.clearApplication();
    this.router.navigateByUrl(RouteName.skippedConfirmation);
  }
}
